<template>
  <div class="pa-4 pb-0">
    <v-alert v-if="isCnpjInactive" type="error" dense class="mb-4">
      <strong>Edição bloqueada:</strong> CNPJ em situação irregular na Receita Federal. Não é possível realizar alterações.
    </v-alert>

    <v-alert v-if="!loadingContacts && hasErrors" type="warning" dense class="mb-4">
      <div class="d-flex align-center">
        <v-icon color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>
        <div>
          <strong>Dados incompletos.</strong> Os seguintes campos não estão preenchidos:
          <ul class="ma-0 mt-1 pl-4">
            <li v-for="field in missingFields" :key="field">{{ field }}</li>
          </ul>
        </div>
      </div>
    </v-alert>
    
    <div class="d-flex justify-end mb-6">
      <template v-if="editMode">
        <v-btn color="red" dark class="mr-2" @click="cancelEdit" :disabled="isCnpjInactive">CANCELAR</v-btn>
        <v-btn color="primary" @click="saveData" :loading="loadingSaveEdit" :disabled="isCnpjInactive">SALVAR EDIÇÕES</v-btn>
      </template>
      <template v-else>
        <div class="d-flex flex-column align-center mr-2">
          <v-tooltip bottom :disabled="!isCnpjInactive">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn 
                  color="secondary" 
                  @click="updateProfileDataScreen"
                  :loading="loadingData"
                  :disabled="!form.document || form.document.length < 14 || isCnpjInactive"
                >
                  SINCRONIZAR DADOS COM RECEITA FEDERAL
                </v-btn>
              </span>
            </template>
            <span>CNPJ inativo. Não é possível realizar alterações</span>
          </v-tooltip>
          <span class="text-caption grey--text mt-1">
            <v-icon x-small class="mr-1" color="grey">mdi-sync</v-icon>
            {{ lastSyncText }}
          </span>
        </div>
        <v-tooltip bottom :disabled="!isCnpjInactive">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn color="primary" @click="editData" :disabled="isCnpjInactive">EDITAR</v-btn>
            </span>
          </template>
          <span>CNPJ inativo. Não é possível realizar alterações</span>
        </v-tooltip>
      </template>
    </div>

    <div class="mb-6">
      <v-row>
        <v-col cols="12" md="2">
          <ImageUploadWithCrop
            v-model="form.logoBase64"
            label="Logo do Veículo"
            :max-size-m-b="5"
            @file-selected="handleLogoSelected"
            @image-removed="handleLogoRemoved"
          />
        </v-col>
      </v-row>
    </div>

    <div class="profile-field">
      <v-row align="center" no-gutters>
        <v-col cols="3">
          <div class="field-label">CNPJ</div>
        </v-col>
        <v-col cols="9">
          <v-text-field
            v-model="form.document"
            :readonly="!editMode"
            :disabled="editMode"
            dense
            :error="hasErrors && (!form.document || form.document.trim() === '')"
            :error-messages="hasErrors && (!form.document || form.document.trim() === '') ? ['Campo obrigatório não preenchido'] : []"
            class="field-input"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-divider class="mt-3"></v-divider>
    </div>

    <div class="profile-field">
      <v-row align="center" no-gutters>
        <v-col cols="3">
          <div class="field-label">Razão Social</div>
        </v-col>
        <v-col cols="9">
          <v-text-field
            v-model="form.name"
            :readonly="!editMode"
            :disabled="editMode"
            dense
            :error="hasErrors && (!form.name || form.name.trim() === '')"
            :error-messages="hasErrors && (!form.name || form.name.trim() === '') ? ['Campo obrigatório não preenchido'] : []"
            class="field-input"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-divider class="mt-3"></v-divider>
    </div>

    <div class="profile-field">
      <v-row align="center" no-gutters>
        <v-col cols="3">
          <div class="field-label">Nome Fantasia</div>
        </v-col>
        <v-col cols="9">
          <v-text-field
            v-model="form.tradingName"
            :readonly="!editMode"
            
            dense
            :error="hasErrors && (!form.tradingName || form.tradingName.trim() === '')"
            :error-messages="hasErrors && (!form.tradingName || form.tradingName.trim() === '') ? ['Campo obrigatório não preenchido'] : []"
            class="field-input"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-divider class="mt-3"></v-divider>
    </div>

    <div class="profile-field">
      <v-row align="center" no-gutters>
        <v-col cols="3">
          <div class="field-label">Nome de exibição</div>
        </v-col>
        <v-col cols="9">
          <v-text-field
            v-model="form.displayName"
            :readonly="!editMode"
            
            dense
            :error="hasErrors && (!form.displayName || form.displayName.trim() === '')"
            :error-messages="hasErrors && (!form.displayName || form.displayName.trim() === '') ? ['Campo obrigatório não preenchido'] : []"
            class="field-input"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-divider class="mt-3"></v-divider>
    </div>

    <div class="profile-field">
      <v-row align="center" no-gutters>
        <v-col cols="3">
          <div class="field-label">Inscrição Estadual</div>
        </v-col>
        <v-col cols="9">
          <v-row align="center" justify="space-between" no-gutters>
            <v-col cols="10">
              <v-text-field
                v-model="form.ie"
                :readonly="!editMode || ieIsenta"
                
                dense
                :error="hasErrors && (!form.ie || form.ie.trim() === '')"
                :error-messages="hasErrors && (!form.ie || form.ie.trim() === '') ? ['Campo obrigatório não preenchido'] : []"
                class="field-input"
              ></v-text-field>
            </v-col>
            <v-col cols="1">
              <v-checkbox
                v-model="ieIsenta"
                label="Isenta"
                :readonly="!editMode"
                :disabled="!editMode"
                dense
                hide-details
                @change="handleIeIsentaChange"
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-divider class="mt-3"></v-divider>
    </div>

    <!-- Sites -->
    <div class="profile-field">
      <v-row no-gutters>
        <v-col cols="3">
          <div class="field-label d-flex align-center" style="height: 40px">
            <v-icon small class="mr-1">mdi-web</v-icon>
            Sites
          </div>
        </v-col>
        <v-col cols="9">
          <div v-if="loadingContacts || loadingWebsites" class="d-flex align-center" style="height: 40px">
            <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2"></v-progress-circular>
            <span class="text-caption grey--text">Carregando...</span>
          </div>

          <template v-else>
            <!-- Sites existentes -->
            <div v-for="site in websites" :key="'site-' + site.id" class="mb-2">
              <v-row align="center" no-gutters>
                <v-col cols="4" class="pr-2">
                  <v-text-field
                    v-model="site.name"
                    placeholder="Nome (ex: Site Principal)"
                    dense
                    outlined
                    hide-details
                    readonly
                    filled
                  ></v-text-field>
                </v-col>
                <v-col cols="6" class="pr-2">
                  <v-text-field
                    v-model="site.contact"
                    placeholder="https://www.exemplo.com.br"
                    dense
                    outlined
                    hide-details
                    readonly
                    filled
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-btn icon small color="red" @click="deleteContact(site.id, 'website')" :disabled="isCnpjInactive" :loading="site._deleting">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <!-- Novos sites a adicionar -->
            <div v-for="(newSite, index) in newWebsites" :key="'new-site-' + index" class="mb-2">
              <v-row align="center" no-gutters>
                <v-col cols="4" class="pr-2">
                  <v-text-field
                    v-model="newSite.name"
                    placeholder="Nome (ex: Site Principal)"
                    dense
                    outlined
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="6" class="pr-2">
                  <v-text-field
                    v-model="newSite.contact"
                    placeholder="https://www.exemplo.com.br"
                    dense
                    outlined
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="2" class="d-flex">
                  <v-btn icon small color="success" @click="saveContact(index, 'website')" :disabled="!newSite.contact || isCnpjInactive" :loading="newSite._saving">
                    <v-icon small>mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon small color="grey" @click="removeNewEntry(index, 'website')">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <!-- Botão adicionar site -->
            <v-btn text small color="primary" @click="addNewEntry('website')" :disabled="isCnpjInactive" class="mt-1">
              <v-icon small left>mdi-plus</v-icon>
              Adicionar site
            </v-btn>
          </template>
        </v-col>
      </v-row>
      <v-divider class="mt-3"></v-divider>
    </div>

    <!-- Redes Sociais -->
    <div class="profile-field">
      <v-row no-gutters>
        <v-col cols="3">
          <div class="field-label d-flex align-center" style="height: 40px">
            <v-icon small class="mr-1">mdi-account-group</v-icon>
            Redes Sociais
          </div>
        </v-col>
        <v-col cols="9">
          <div v-if="loadingContacts || loadingSocialMedia" class="d-flex align-center" style="height: 40px">
            <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2"></v-progress-circular>
            <span class="text-caption grey--text">Carregando...</span>
          </div>

          <template v-else>
            <!-- Redes sociais existentes -->
            <div v-for="social in socialMedia" :key="'social-' + social.id" class="mb-2">
              <v-row align="center" no-gutters>
                <v-col cols="4" class="pr-2">
                  <v-text-field
                    v-model="social.name"
                    placeholder="Nome (ex: Instagram Oficial)"
                    dense
                    outlined
                    hide-details
                    readonly
                    filled
                  ></v-text-field>
                </v-col>
                <v-col cols="6" class="pr-2">
                  <v-text-field
                    v-model="social.contact"
                    placeholder="@empresa ou link"
                    dense
                    outlined
                    hide-details
                    readonly
                    filled
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-btn icon small color="red" @click="deleteContact(social.id, 'social')" :disabled="isCnpjInactive" :loading="social._deleting">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <!-- Novas redes sociais a adicionar -->
            <div v-for="(newSocial, index) in newSocialMedia" :key="'new-social-' + index" class="mb-2">
              <v-row align="center" no-gutters>
                <v-col cols="4" class="pr-2">
                  <v-text-field
                    v-model="newSocial.name"
                    placeholder="Nome (ex: Instagram Oficial)"
                    dense
                    outlined
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="6" class="pr-2">
                  <v-text-field
                    v-model="newSocial.contact"
                    placeholder="@empresa ou link"
                    dense
                    outlined
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="2" class="d-flex">
                  <v-btn icon small color="success" @click="saveContact(index, 'social')" :disabled="!newSocial.contact || isCnpjInactive" :loading="newSocial._saving">
                    <v-icon small>mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon small color="grey" @click="removeNewEntry(index, 'social')">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <!-- Botão adicionar rede social -->
            <v-btn text small color="primary" @click="addNewEntry('social')" :disabled="isCnpjInactive" class="mt-1">
              <v-icon small left>mdi-plus</v-icon>
              Adicionar rede social
            </v-btn>
          </template>
        </v-col>
      </v-row>
      <v-divider class="mt-3"></v-divider>
    </div>

    <v-dialog v-model="showUnsavedChangesModal" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Alterações não salvas
        </v-card-title>
        <v-card-text>
          Você tem alterações não salvas. Deseja realmente sair sem salvar?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelLeave">
            Permanecer na página
          </v-btn>
          <v-btn color="red" text @click="confirmLeave">
            Sair sem salvar
          </v-btn>
          <v-btn color="primary" @click="saveAndLeave" :loading="loadingSaveEdit">
            Salvar e sair
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="showSnackbarSuccess"
      :timeout="2000"
      color="success"
      top
    >
      {{ messageSuccessSnackbar }}
    </v-snackbar>

    <v-snackbar v-model="showSnackbarError" :timeout="2000" color="red" top>
      {{ messageErrorSnackbar }}
    </v-snackbar>
  </div>
</template>

<script>
import http from 'axios'
import ImageUploadWithCrop from '@/components/globalComponents/ImageUploadWithCrop.vue'

export default {
  name: 'ProfileDataForm',

  components: {
    ImageUploadWithCrop
  },
  
  props: {
    profileData: {
      type: Object,
      default: () => ({})
    },
    profileId: {
      type: [String, Number],
      required: true
    },
    isCnpjInactive: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      editMode: false,
      loadingData: false,
      loadingSaveEdit: false,
      originalForm: {},
      clonedFormData: {},
      dataUpdatedFromRFB: false,
      showUnsavedChangesModal: false,
      pendingRoute: null,
      showSnackbarSuccess: false,
      messageSuccessSnackbar: "",
      showSnackbarError: false,
      messageErrorSnackbar: "",
      dataLoaded: false,
      ieIsenta: false,
      loadingContacts: false,
      loadingWebsites: false,
      loadingSocialMedia: false,
      websites: [],
      socialMedia: [],
      rfbSyncAt: null,
      newWebsites: [],
      newSocialMedia: [],
      form: {
        name: '',
        tradingName: '',
        displayName: '',
        document: '',
        location: 'BRA',
        ie: '',
        logoBase64: null,
        logoFile: null,
        logoFilename: null
      }
    }
  },

  computed: {
    missingFields() {
      if (!this.dataLoaded) return [];
      
      const missing = [];
      if (!this.form.name || this.form.name.trim() === '') missing.push('Razão Social');
      if (!this.form.tradingName || this.form.tradingName.trim() === '') missing.push('Nome Fantasia');
      if (!this.form.displayName || this.form.displayName.trim() === '') missing.push('Nome de exibição');
      if (!this.form.ie || this.form.ie.trim() === '') missing.push('Inscrição Estadual');
      if (!this.loadingContacts && this.websites.length === 0 && this.newWebsites.length === 0) missing.push('Sites');
      if (!this.loadingContacts && this.socialMedia.length === 0 && this.newSocialMedia.length === 0) missing.push('Redes Sociais');
      return missing;
    },
    hasErrors() {
      return this.dataLoaded && this.missingFields.length > 0;
    },
    lastSyncText() {
      if (!this.rfbSyncAt) return 'Nunca sincronizado com a Receita Federal';
      try {
        const parts = this.rfbSyncAt.split(' ');
        const [year, month, day] = parts[0].split('-');
        const time = parts[1] ? parts[1].substring(0, 5) : '';
        return `Última sincronização: ${day}/${month}/${year} às ${time}`;
      } catch {
        return 'Nunca sincronizado com a Receita Federal';
      }
    }
  },

  watch: {
    profileData: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          this.form = { ...this.form, ...newData }
          
          
          if (newData.logo && !this.form.logoBase64) {
            this.form.logoBase64 = newData.logo
          }

          if (newData.ie && newData.ie.toUpperCase() === 'ISENTA') {
            this.ieIsenta = true;
          }

          if (newData.rfbSyncAt !== undefined) {

            console.log('newData', newData)
            this.rfbSyncAt = newData.rfbSyncAt;
          }
          
          this.dataLoaded = true;
        }
      },
      immediate: true,
      deep: true
    },
    form: {
      handler(newForm, oldForm) {
        if (this.dataUpdatedFromRFB && oldForm && Object.keys(oldForm).length > 0) {
          const hasChanges = JSON.stringify(newForm) !== JSON.stringify(this.clonedFormData);
          if (hasChanges && !this.editMode) {
            this.originalForm = { ...this.clonedFormData };
            this.editMode = true;
          }
        }
      },
      deep: true
    }
  },

  beforeRouteLeave(to, from, next) {
    if (this.editMode) {
      this.pendingRoute = { to, from, next };
      this.showUnsavedChangesModal = true;
      return false; 
    } else {
      next();
    }
  },

  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.loadContacts();
  },

  beforeDestroy() {
    
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },

  methods: {
    async loadContacts() {
      try {
        this.loadingContacts = true;
        const response = await this.$axios.get(`contacts/profile`, {
          params: { id: this.profileId }
        });

        if (response.data && response.data.data) {
          const contacts = response.data.data;
          this.websites = contacts
            .filter(c => c.contactTypeId === 4)
            .map(c => ({ ...c, _deleting: false }));
          this.socialMedia = contacts
            .filter(c => c.contactTypeId === 10)
            .map(c => ({ ...c, _deleting: false }));
        }
      } catch (error) {
        console.error('Erro ao carregar contatos:', error);
      } finally {
        this.loadingContacts = false;
      }
    },

    async reloadContactsByType(type) {
      try {
        if (type === 'website') {
          this.loadingWebsites = true;
        } else {
          this.loadingSocialMedia = true;
        }

        const response = await this.$axios.get(`contacts/profile`, {
          params: { id: this.profileId }
        });

        if (response.data && response.data.data) {
          const contacts = response.data.data;
          if (type === 'website') {
            this.websites = contacts
              .filter(c => c.contactTypeId === 4)
              .map(c => ({ ...c, _deleting: false }));
          } else {
            this.socialMedia = contacts
              .filter(c => c.contactTypeId === 10)
              .map(c => ({ ...c, _deleting: false }));
          }
        }
      } catch (error) {
        console.error('Erro ao recarregar contatos:', error);
      } finally {
        if (type === 'website') {
          this.loadingWebsites = false;
        } else {
          this.loadingSocialMedia = false;
        }
      }
    },

    addNewEntry(type) {
      const entry = { name: '', contact: '', _saving: false };
      if (type === 'website') {
        this.newWebsites.push(entry);
      } else {
        this.newSocialMedia.push(entry);
      }
    },

    removeNewEntry(index, type) {
      if (type === 'website') {
        this.newWebsites.splice(index, 1);
      } else {
        this.newSocialMedia.splice(index, 1);
      }
    },

    async saveContact(index, type) {
      const isWebsite = type === 'website';
      const list = isWebsite ? this.newWebsites : this.newSocialMedia;
      const entry = list[index];

      if (!entry.contact) return;

      try {
        this.$set(entry, '_saving', true);

        const payload = {
          contactTypeId: isWebsite ? 4 : 10,
          contact: entry.contact,
          name: entry.name || (isWebsite ? 'Site' : 'Rede Social'),
          profileId: this.profileId,
          status: 1
        };

        await this.$axios.post(`contacts`, payload);

        list.splice(index, 1);
        await this.reloadContactsByType(type);
        this.showToastSuccess(isWebsite ? 'Site adicionado com sucesso!' : 'Rede social adicionada com sucesso!');
      } catch (error) {
        console.error('Erro ao salvar contato:', error);
        this.showToastError('Erro ao salvar. Tente novamente!');
      } finally {
        if (list[index]) {
          this.$set(list[index], '_saving', false);
        }
      }
    },

    async deleteContact(contactId, type) {
      const isWebsite = type === 'website';
      const list = isWebsite ? this.websites : this.socialMedia;
      const item = list.find(c => c.id === contactId);

      if (!item) return;

      try {
        this.$set(item, '_deleting', true);
        await this.$axios.delete(`contacts/${contactId}`);
        await this.reloadContactsByType(type);
        this.showToastSuccess(isWebsite ? 'Site removido com sucesso!' : 'Rede social removida com sucesso!');
      } catch (error) {
        console.error('Erro ao deletar contato:', error);
        this.showToastError('Erro ao remover. Tente novamente!');
        if (item) this.$set(item, '_deleting', false);
      }
    },

    handleIeIsentaChange() {
      if (this.ieIsenta) {
        this.form.ie = 'ISENTA';
      } else {
        this.form.ie = '';
      }
    },

    handleLogoSelected(data) {
      
      if (!this.editMode) {
        
        this.originalForm = { ...this.form };
        this.editMode = true;
      }
      
      this.form.logoBase64 = data.base64;
      this.form.logoFile = data.file;
      this.form.logoFilename = data.file.name;
    },

    handleLogoRemoved() {
      
      if (!this.editMode) {
        
        this.originalForm = { ...this.form };
        this.editMode = true;
      }
      
      this.form.logoBase64 = null;
      this.form.logoFile = null;
      this.form.logoFilename = null;
    },

    editData() {
      this.originalForm = { ...this.form };
      this.editMode = true;
    },

    cancelEdit() {
      this.form = { ...this.originalForm };
      this.editMode = false;
      this.dataUpdatedFromRFB = false;
    },

    async saveData() {
      this.loadingSaveEdit = true;
      try {
        const payload = {
          id: this.profileId,
          name: this.form.name,
          displayName: this.form.displayName,
          tradingName: this.form.tradingName,
          ie: this.form.ie,
        };

        
        if (this.form.logoBase64 && this.form.logoFilename) {
          payload.file = {
            filename: this.form.logoFilename,
            base64: this.form.logoBase64
          };
        }

        const response = await this.$axios.put(`profiles`, payload);
        
        this.showToastSuccess("Dados salvos com sucesso!");
        this.loadingSaveEdit = false;
        this.editMode = false;
        this.originalForm = {};
        this.dataUpdatedFromRFB = false;
        
      } catch (error) {
        console.error('Erro ao salvar dados:', error);
        this.loadingSaveEdit = false;
        this.showToastError("Erro ao salvar dados. Tente novamente!");
      }
    },

    async updateProfileDataScreen() {
      try {
        if (!this.form.document || this.form.document.length < 14) {
          this.showToastError('CNPJ inválido ou não informado!');
          return;
        }

        const cnpjFormatted = this.form.document.replace(/[^\d]/g, "");
        this.loadingData = true;
        
        const response = await this.$axios.post(`assets/checker/documentId`, {
          input: "documentId",
          value: cnpjFormatted,
          profileId: this.profileId,
          mv: true
        });
        
        if (response.data && response.data.data && response.data.data.companyData) {
          const documentData = response.data.data.companyData;
          this.clonedFormData = { ...this.form };
          this.dataUpdatedFromRFB = true;
  
          this.form.name = documentData.nome || this.form.name;
          
          if (!this.form.tradingName || this.form.tradingName.length === 0) {
            this.form.tradingName = this.form.displayName || documentData.nome;
          }
          
          const now = new Date();
          const pad = (n) => String(n).padStart(2, '0');
          this.rfbSyncAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

          this.showToastSuccess("Dados atualizados com sucesso!");
        } else {
          this.showToastError("Dados não encontrados para este CNPJ!");
        }
        
      } catch (error) {
        console.error('Erro ao atualizar dados:', error);
        this.showToastError("Erro ao buscar dados. Tente novamente!");
      } finally {
        this.loadingData = false;
      }
    },

    showToastSuccess(message) {
      this.messageSuccessSnackbar = message;
      this.showSnackbarSuccess = true;
    },

    showToastError(message) {
      this.messageErrorSnackbar = message;
      this.showSnackbarError = true;
    },

    handleBeforeUnload(event) {
      if (this.editMode) {
        event.preventDefault();
        event.returnValue = 'Você tem alterações não salvas. Deseja realmente sair?';
        return 'Você tem alterações não salvas. Deseja realmente sair?';
      }
    },

    cancelLeave() {
      this.showUnsavedChangesModal = false;
      this.pendingRoute = null;
    },

    confirmLeave() {
      this.showUnsavedChangesModal = false;
      this.editMode = false;
      this.dataUpdatedFromRFB = false;
      if (this.pendingRoute) {
        const { next } = this.pendingRoute;
        this.pendingRoute = null;
        next();
      }
    },

    async saveAndLeave() {
      try {
        await this.saveData();
        this.showUnsavedChangesModal = false;
        if (this.pendingRoute) {
          const { next } = this.pendingRoute;
          this.pendingRoute = null;
          next();
        }
      } catch (error) {
        
        console.error('Erro ao salvar antes de sair:', error);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
* {
  font-family: 'Roboto', sans-serif !important;
}

.profile-field {
  padding: 16px 0;
  
  .field-label {
    font-size: 14px;
    color: #666;
    font-weight: 400;
    padding-right: 16px;
  }
  
  .field-input {
    .v-input__control {
      min-height: 40px;
    }
    
    .v-text-field__details {
      display: none;
    }
    

  }
}
</style>